import { useTranslation } from 'react-i18next';
import type { ILocationProductivityRow } from '@interfaces/IProductivity';
import { MapPinIcon } from '@utils/icons';
import { formatNumber } from '@utils/formatNumber';
import { formatPercentCell } from '../../../../utils/formatPercent';
import { CardHeader } from '../../../../shared/CardHeader/CardHeader';
import { ConfidencePill } from '../../Table/ConfidencePill/ConfidencePill';
import { ProductivityBar } from '../../Table/ProductivityBar/ProductivityBar';
import { makeSortableTh } from '../../Table/Th/makeSortableTh';
import { NameCell } from '../../Table/NameCell';
import { BreakdownTableFooter } from '../../Table/Footer';
import { useSortableRows } from '../../Table/useSortableRows';
import { usePagination } from '../../Table/usePagination';
import table from '../../Table/breakdownTable.module.css';
import styles from './LocationPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ILocationPanelProps {
  rows: ILocationProductivityRow[];
  onRowClick: (row: ILocationProductivityRow) => void;
}

export function LocationPanel({ rows, onRowClick }: ILocationPanelProps) {
  const { t } = useTranslation();
  const sort = useSortableRows(rows, { key: 'location', dir: 'asc' });
  const pager = usePagination(sort.sorted, sort.sortSignature);
  const th = makeSortableTh('location', sort, t);

  return (
    <>
      <CardHeader
        icon={<MapPinIcon size={16} />}
        title={t('productivity.location.title')}
        meta={t('productivity.location.meta')}
      />

      <ScrollArea orientation="horizontal">
        <table className={table.table}>
          <colgroup>
            <col className={styles.colLocation} />
            <col className={styles.colProductivity} />
            <col className={styles.colActual} />
            <col className={styles.colSpi} />
            <col className={styles.colUtil} />
            <col className={styles.colConfidence} />
            <col className={styles.colIssue} />
          </colgroup>
          <thead>
            <tr>
              {th('location', 'productivity.location.columns.location')}
              {th('productivity', 'productivity.location.columns.productivity')}
              {th('actualWorkDays', 'productivity.location.columns.actualWorkDays')}
              {th('spi', 'productivity.location.columns.spi')}
              {th('util', 'productivity.location.columns.util')}
              {th('confidence', 'productivity.location.columns.confidence')}
              <th className={table.th}>{t('productivity.location.columns.status')}</th>
            </tr>
          </thead>
          <tbody>
            {pager.visible.map((row) => (
              <tr key={row.id} className={`${table.tr} ${table.trAction}`} onClick={() => onRowClick(row)}>
                <NameCell value={row.location} onActivate={() => onRowClick(row)} />
                <td className={table.td}>
                  <ProductivityBar value={row.productivity} />
                </td>
                <td className={table.td}>{formatNumber(row.actualWorkDays)}</td>
                <td className={table.td}>{formatPercentCell(row.spi)}</td>
                <td className={table.td}>{formatPercentCell(row.util)}</td>
                <td className={table.td}>
                  <ConfidencePill level={row.confidence} reason={row.confidenceReason} />
                </td>
                <td className={`${table.td} ${table.issue}`}>
                  {row.status ? t(`productivity.status.${row.status}`) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <BreakdownTableFooter pager={pager} />
        </table>
      </ScrollArea>
    </>
  );
}
